import React, { useContext, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { CardSession, Typography } from '@telus/koodo-ui-kit'
import { APPLICATION_STATE, ApplicationContext } from '@apps/contexts/ApplicationContext'
import { logOut } from '@apps/redux/features/CommonSlice'
import { getById, getBySlug } from '@apps/utils/contentful'
import ConsumerActivationPaths from '@sites/consumer/activation/ConsumerActivationPaths'
import DealerActivationPaths from '@sites/dealer/activation/DealerActivationPaths'
import Sites from '@sites/index'

const ConsumerSessionCard = () => {
  const dispatch = useDispatch()

  const {
    [APPLICATION_STATE.CURRENT_LANGUAGE]: language,
    [APPLICATION_STATE.SERVICES]: brandServices
  } = useContext(ApplicationContext)

  const {
    userType,
    user: { email },
    content: { pageContent }
  } = useSelector((state) => state.common)

  const isDealer = userType === Sites.dealer

  // Construct during render — these path classes call `useLocation()` in
  // their constructor chain, so `new ConsumerActivationPaths()` inside an
  // event handler triggers "Invalid hook call".
  const consumerActivationPaths = new ConsumerActivationPaths()
  const dealerActivationPaths = new DealerActivationPaths()

  const text = useMemo(() => {
    if (!pageContent || !language) return {}
    const commonElements = getById(pageContent, 'commonElements')?.[language]
    const commonShort = commonElements?.shortTextFields
    return {
      sessionCardLabel: getBySlug(commonShort, 'sessionCardLabel')?.value,
      logoutLabel: getBySlug(commonShort, 'header-logout')?.value
    }
  }, [pageContent, language])

  const handleLogout = async () => {
    const basePath = isDealer
      ? dealerActivationPaths.Base
      : consumerActivationPaths.Base
    const redirectToLandingPage = () => {
      window.location.href = basePath
    }
    const { retrieveCookie, deleteCookie } = brandServices || {}
    const session = (retrieveCookie && (await retrieveCookie())) || {}
    if (session.userSessionId) {
      await deleteCookie()
      dispatch(logOut())
      redirectToLandingPage()
    } else {
      dispatch(logOut())
      redirectToLandingPage()
    }
  }

  const handleSessionCardKeyDown = (event) => {
    if (event.defaultPrevented || event.repeat) return
    if (!['Enter', ' ', 'Spacebar'].includes(event.key)) return

    const actionElement = event.target?.closest?.('[role="link"], a, button')
    const actionText = `${actionElement?.textContent || ''}`.replace(/\s+/g, ' ').trim()
    const logoutText = `${text.logoutLabel || 'Log out'}`.replace(/\s+/g, ' ').trim()

    if (!actionElement || actionText.toLowerCase() !== logoutText.toLowerCase()) return

    event.preventDefault()
    event.stopPropagation()
    handleLogout()
  }

  if (!email) return null

  return (
    <div onKeyDownCapture={handleSessionCardKeyDown}>
      <CardSession
        onAction={handleLogout}
        actionLabel={text.logoutLabel}
        className="kpre-consumer-session-card"
        tokens={{ paddingTop: 24, paddingBottom: 24 }}
      >
        {text.sessionCardLabel}{' '}
        <Typography variant={{ bold: true }}>{email}</Typography>
        .
      </CardSession>
    </div>
  )
}

export default ConsumerSessionCard
